/**
 * CulturalIntelligencePanel Component
 *
 * Compact, collapsible cultural intelligence display for concert events.
 * Focuses on UNIQUE content not shown elsewhere:
 * - Headlines/News (from GNews)
 * - Mood/Vibe indicator (from Last.fm tags)
 * - Fan engagement stats
 *
 * Avoids duplicating:
 * - Artist bio (shown in ArtistInfoWidget)
 * - Track list (shown in FeaturedTracksPanel)
 *
 * Switches between three modes based on days until event:
 * - STORY MODE (>30 days): Fan stats + headlines
 * - MOMENTUM MODE (7-30 days): Mood + trending buzz + headlines
 * - LIVE VIBE MODE (<=7 days): Tonight's vibe hero + headlines
 *
 * Design: Dark glass, purple glow, collapsible, mobile-first
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Sparkles,
  TrendingUp,
  Zap,
  Newspaper,
  Users,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Activity,
} from 'lucide-react';
import {
  getFanTrends,
  formatNumber,
  getMoodColorClass,
  type FanTrendData,
} from '../services/lastfmService';
import {
  getHeadlines,
  formatRelativeTime,
  type HeadlinesResponse,
} from '../services/gnewsService';
import { parseLocalDate } from '../utils/dateUtils';

// Mode type
type IntelligenceMode = 'story' | 'momentum' | 'live';

interface CulturalIntelligencePanelProps {
  artistName: string;
  eventDate: string; // ISO date string
  /** Theme accent color */
  accentColor?: 'purple' | 'orange' | 'sky' | 'red' | 'brand';
  /** Start collapsed (default: true on mobile) */
  defaultCollapsed?: boolean;
}

/**
 * Calculate days until event
 * Uses parseLocalDate to avoid timezone bugs
 */
function getDaysUntilEvent(eventDate: string): number {
  const event = parseLocalDate(eventDate);
  if (!event) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  event.setHours(0, 0, 0, 0);
  const diffMs = event.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Determine which mode to display
 */
function getIntelligenceMode(daysUntil: number): IntelligenceMode {
  if (daysUntil > 30) return 'story';
  if (daysUntil > 7) return 'momentum';
  return 'live';
}

/**
 * Mode configuration
 */
const MODE_CONFIG: Record<
  IntelligenceMode,
  {
    icon: typeof Sparkles;
    title: string;
    gradient: string;
  }
> = {
  story: {
    icon: Sparkles,
    title: 'Cultural Intel',
    gradient: 'from-purple-500/15 via-violet-500/10 to-transparent',
  },
  momentum: {
    icon: TrendingUp,
    title: 'Trending Now',
    gradient: 'from-amber-500/15 via-orange-500/10 to-transparent',
  },
  live: {
    icon: Zap,
    title: 'Live Vibe',
    gradient: 'from-cyan-500/15 via-blue-500/10 to-transparent',
  },
};

/**
 * Color classes based on accent
 */
const colorClasses = {
  purple: {
    accent: 'bg-purple-500',
    border: 'border-purple-500/20',
    glow: 'shadow-[0_0_20px_rgba(168,85,247,0.15)]',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300',
  },
  orange: {
    accent: 'bg-orange-500',
    border: 'border-orange-500/20',
    glow: 'shadow-[0_0_20px_rgba(249,115,22,0.15)]',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300',
  },
  sky: {
    accent: 'bg-sky-500',
    border: 'border-sky-500/20',
    glow: 'shadow-[0_0_20px_rgba(14,165,233,0.15)]',
    text: 'text-sky-400',
    badge: 'bg-sky-500/20 text-sky-300',
  },
  red: {
    accent: 'bg-red-500',
    border: 'border-red-500/20',
    glow: 'shadow-[0_0_20px_rgba(239,68,68,0.15)]',
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300',
  },
  brand: {
    accent: 'bg-brand-500',
    border: 'border-brand-500/20',
    glow: 'shadow-[0_0_20px_rgba(var(--accent-primary-rgb),0.15)]',
    text: 'text-brand-400',
    badge: 'bg-brand-500/20 text-brand-300',
  },
};

/**
 * Headlines Section (shared across all modes)
 */
const HeadlinesSection: React.FC<{
  headlines: HeadlinesResponse | null;
  colors: typeof colorClasses.purple;
  maxItems?: number;
}> = ({ headlines, colors, maxItems = 2 }) => {
  if (!headlines || headlines.articles.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Newspaper size={12} className={colors.text} />
        <span className="text-[10px] text-white/50 uppercase tracking-wider">
          Latest
        </span>
      </div>
      {headlines.articles.slice(0, maxItems).map((article, i) => (
        <a
          key={i}
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2 bg-white/[0.02] rounded-lg px-2.5 py-2 hover:bg-white/[0.05] transition-all duration-150 group"
        >
          <span className="text-xs text-white/80 flex-1 line-clamp-2 leading-relaxed">
            {article.title}
          </span>
          <ExternalLink
            size={10}
            className="text-white/20 group-hover:text-white/50 flex-shrink-0 mt-0.5"
          />
        </a>
      ))}
    </div>
  );
};

/**
 * Compact Stats Row
 */
const StatsRow: React.FC<{
  fanTrends: FanTrendData | null;
  colors: typeof colorClasses.purple;
}> = ({ fanTrends, colors }) => {
  if (!fanTrends) return null;

  return (
    <div className="flex gap-2">
      <div className="flex-1 bg-white/[0.03] rounded-lg px-2.5 py-2 text-center">
        <div className={`text-sm font-bold ${colors.text}`}>
          {formatNumber(fanTrends.totalListeners)}
        </div>
        <div className="text-[9px] text-white/40 uppercase">Fans</div>
      </div>
      <div className="flex-1 bg-white/[0.03] rounded-lg px-2.5 py-2 text-center">
        <div className={`text-sm font-bold ${colors.text}`}>
          {formatNumber(fanTrends.totalPlaycount)}
        </div>
        <div className="text-[9px] text-white/40 uppercase">Plays</div>
      </div>
      {fanTrends.dominantMood && (
        <div className="flex-1 bg-white/[0.03] rounded-lg px-2.5 py-2 text-center">
          <div className={`text-sm font-bold ${getMoodColorClass(fanTrends.dominantMood)}`}>
            {fanTrends.dominantMood}
          </div>
          <div className="text-[9px] text-white/40 uppercase">Mood</div>
        </div>
      )}
    </div>
  );
};

/**
 * Live Vibe Hero (for events within 7 days)
 */
const LiveVibeHero: React.FC<{
  mood: string;
  daysUntil: number;
  colors: typeof colorClasses.purple;
}> = ({ mood, daysUntil, colors }) => {
  const timeLabel =
    daysUntil === 0
      ? 'TONIGHT'
      : daysUntil === 1
      ? 'TOMORROW'
      : `${daysUntil} DAYS`;

  return (
    <div className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] rounded-lg p-3 text-center">
      <div className="flex items-center justify-center gap-2 mb-1">
        <Activity size={14} className={colors.text} />
        <span className={`text-[10px] font-medium ${colors.badge} px-1.5 py-0.5 rounded`}>
          {timeLabel}
        </span>
      </div>
      <div className={`text-xl font-black ${getMoodColorClass(mood)}`}>
        {mood}
      </div>
      <div className="text-[10px] text-white/40 mt-0.5">Vibe Check</div>
    </div>
  );
};

/**
 * Main CulturalIntelligencePanel Component
 */
export const CulturalIntelligencePanel: React.FC<CulturalIntelligencePanelProps> = ({
  artistName,
  eventDate,
  accentColor = 'purple',
  defaultCollapsed = true,
}) => {
  const [fanTrends, setFanTrends] = useState<FanTrendData | null>(null);
  const [headlines, setHeadlines] = useState<HeadlinesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);

  // Calculate mode
  const daysUntil = useMemo(() => getDaysUntilEvent(eventDate), [eventDate]);
  const mode = useMemo(() => getIntelligenceMode(daysUntil), [daysUntil]);
  const modeConfig = MODE_CONFIG[mode];
  const colors = colorClasses[accentColor];

  // Fetch data
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const [trendsData, headlinesData] = await Promise.all([
          getFanTrends(artistName),
          getHeadlines(artistName, 3),
        ]);

        if (!cancelled) {
          setFanTrends(trendsData);
          setHeadlines(headlinesData);
        }
      } catch (err) {
        console.error('[CulturalIntelligence] Fetch error:', err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [artistName]);

  // Don't render if no data available
  if (!isLoading && !fanTrends && (!headlines || headlines.articles.length === 0)) {
    return null;
  }

  const ModeIcon = modeConfig.icon;

  return (
    <div
      className={`
        bg-gradient-to-br ${modeConfig.gradient}
        backdrop-blur-sm rounded-xl border ${colors.border}
        transition-all duration-200 overflow-hidden
        ${isExpanded ? colors.glow : ''}
      `}
    >
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-all duration-150"
      >
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 ${colors.accent} rounded-md flex items-center justify-center`}>
            <ModeIcon size={14} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wide">
            {modeConfig.title}
          </span>
          {!isExpanded && fanTrends?.dominantMood && (
            <span className={`text-xs ${getMoodColorClass(fanTrends.dominantMood)} ml-1`}>
              • {fanTrends.dominantMood}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 size={12} className={`${colors.text} animate-spin`} />}
          {isExpanded ? (
            <ChevronUp size={16} className="text-white/40" />
          ) : (
            <ChevronDown size={16} className="text-white/40" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && !isLoading && (
        <div className="px-3 pb-3 space-y-3">
          {/* Live Vibe Hero (only for events within 7 days) */}
          {mode === 'live' && fanTrends?.dominantMood && (
            <LiveVibeHero
              mood={fanTrends.dominantMood}
              daysUntil={daysUntil}
              colors={colors}
            />
          )}

          {/* Stats (for story and momentum modes) */}
          {mode !== 'live' && <StatsRow fanTrends={fanTrends} colors={colors} />}

          {/* Headlines */}
          <HeadlinesSection
            headlines={headlines}
            colors={colors}
            maxItems={mode === 'live' ? 1 : 2}
          />

          {/* Tags (if available) */}
          {fanTrends && fanTrends.topTags.length > 0 && mode !== 'live' && (
            <div className="flex flex-wrap gap-1">
              {fanTrends.topTags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className={`px-1.5 py-0.5 rounded text-[9px] ${colors.badge}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CulturalIntelligencePanel;
